import { Layout, theme } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useEventBus } from '@/hooks/useEventBus';

import { getProjectPublicInfo } from '@/utils/services/ProjectApiService';

import LoadingPageContent from '../components/loading-page-content/LoadingPageContent';
import { AuthContext } from '../context/AuthContext';
import useAuthorization from '../hooks/useAuthorization';
import useFilter from '../hooks/useFilter';
import { LOCALSTORAGE } from '../utils/constants/AppConstants';
import Header from './header/Header';
import Sidebar from './sidebar/Sidebar';

import './AppLayout.scss';

export const SYSTEM_USAGE_IDS: string[] = ['1d6e24df-f26e-4507-b076-d855e73b13a2', '4d298b4f-6217-4ea9-98b2-0524c968ea4c'];

type SendProjectIdPayload = {
  projectId: string | null;
  label?: string;
};

const { Content } = Layout;

const contentStyle: React.CSSProperties = {
  padding: '16px 20px',
  overflow: 'auto',
  height: 'calc(100vh - 80px)'
};

const App: React.FC = () => {
  const statusCollapsed = JSON.parse(localStorage.getItem(LOCALSTORAGE.STATUS_COLLAPSED) ?? 'false');
  const [collapsed, setCollapsed] = useState(statusCollapsed);
  const { user } = useAuthorization();
  const { filteredValues } = useFilter();
  const eventBus = useEventBus();
  const { t } = useTranslation();

  const [labelTag, setLabelTag] = useState<string | null>(null);

  const {
    token: { colorBgContainer }
  } = theme.useToken();

  const toggleSider = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem(LOCALSTORAGE.STATUS_COLLAPSED, JSON.stringify(newCollapsed));
  };

  const dataContext = useMemo(() => {
    return {
      user,
      isAuthenticated: true,
      filteredValues
    };
  }, [user, filteredValues]);

  const handleGetProjectPublicInfo = useCallback(async (id: string | null) => {
    if (!id) {
      setLabelTag(null);
      return;
    }

    try {
      const { data } = await getProjectPublicInfo(String(id));
      if (data?.id) {
        setLabelTag(data.projectMemoSupportTypeId && SYSTEM_USAGE_IDS.includes(data.projectMemoSupportTypeId) ? t('sider:system_usage') : null);
      }
    } catch (err) {
      setLabelTag(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = eventBus.on('sendProjectId', (payload?: SendProjectIdPayload) => {
      if (!payload) {
        setLabelTag(null);
        return;
      }

      const { projectId, label } = payload;

      if (label) {
        setLabelTag(label);
        return;
      }
      handleGetProjectPublicInfo(projectId);
    });

    return unsubscribe;
  }, [eventBus]);

  if (!user) {
    return <></>;
  }

  return (
    <AuthContext.Provider value={dataContext}>
      <Layout style={{ minHeight: '100vh' }} hasSider>
        <Sidebar toggleSider={toggleSider} collapsed={collapsed} setCollapsed={setCollapsed} />
        <Layout className='site-layout'>
          <Header colorBgContainer={colorBgContainer} labelTag={labelTag} />
          <Content
            style={{ ...contentStyle, ...(labelTag && { backgroundColor: '#E7F1EF' }) }}
            className={`relative ${labelTag ? '[&_.form-add-task-of-project>div]:bg-[#E7F1EF] [&_.comment-widget]:!bg-[#ffffff]' : ''}`}
          >
            <Outlet />
            <LoadingPageContent />
          </Content>
        </Layout>
      </Layout>
    </AuthContext.Provider>
  );
};

export default App;
